import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { httpClient } from '../../utils/apiCall';
import { API_ENDPOINTS } from '../../config/api';
import {
  type ApiQuote,
  ALL_COLUMNS,
  INITIALLY_HIDDEN,
  SupplierList,
  fmtDate,
  fmtNum,
} from './quoteRoundsView';
import {
  ExchangeRatePrompt,
  ManualRateNote,
  needsRatePrompt,
  type ExchangeRateInfo,
  type ManualRates,
} from './ExchangeRatePrompt';

// ─── Public Types (parent geri uyumluluğu) ────────────────────────────────────

export interface ModalRequest {
  id: number;
  itemName: string;
  itemCode?: string;
  requestedQuantity?: number | undefined;
  requestedDeliveryDate?: string | null | undefined;
}

// ─── Backend response tipleri ────────────────────────────────────────────────

interface ApiHeader {
  id: number;
  itemCode?: string | null;
  itemName?: string | null;
  requestedQuantity: number;
  requestedDeliveryDate?: string | null;
}

interface ApiDetail {
  header: ApiHeader;
  quotes: ApiQuote[];
  exchangeRates?: ExchangeRateInfo;
}

// ─── Modal Inner ──────────────────────────────────────────────────────────────

const ModalInner: React.FC<{ request: ModalRequest; onClose: () => void }> = ({ request, onClose }) => {
  const [detail, setDetail] = useState<ApiDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => new Set(INITIALLY_HIDDEN));
  const [chooserOpen, setChooserOpen] = useState(false);
  const chooserRef = useRef<HTMLDivElement>(null);
  // Netsis'te bugünün kuru yoksa kullanıcının bu açılış için girdiği kurlar (kaydedilmez)
  const [manualRates, setManualRates] = useState<ManualRates>({});
  const [ratePromptInfo, setRatePromptInfo] = useState<ExchangeRateInfo | null>(null);

  useEffect(() => {
    if (!chooserOpen) return;
    const onClick = (e: MouseEvent) => {
      if (chooserRef.current && !chooserRef.current.contains(e.target as Node)) {
        setChooserOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [chooserOpen]);

  const toggleColumn = (colId: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  };

  // ESC kapanış
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    httpClient
      .get<ApiDetail>(API_ENDPOINTS.PO_REQUEST.DETAIL_FULL(request.id), { params: manualRates })
      .then(r => {
        if (cancelled) return;
        if (r.success && r.data) {
          setDetail(r.data);
          if (Object.keys(manualRates).length === 0 && needsRatePrompt(r.data.exchangeRates)) {
            setRatePromptInfo(r.data.exchangeRates!);
          }
        } else setError(r.message || 'Detay yüklenemedi');
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Detay yüklenemedi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request.id, manualRates]);

  const header = detail?.header;
  const quotes = useMemo(() => detail?.quotes ?? [], [detail]);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-7xl flex flex-col border border-gray-200 dark:border-gray-700"
        style={{ height: '80vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Başlık */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate flex items-center gap-2">
              #{header?.id ?? request.id}
              {(header?.itemCode ?? request.itemCode) && (
                <span className="text-gray-500 dark:text-gray-400 font-normal">
                  · {header?.itemCode ?? request.itemCode}
                </span>
              )}
              <span>· {header?.itemName ?? request.itemName}</span>
            </p>
            <div className="flex flex-wrap gap-x-4 text-xs text-gray-900 dark:text-white mt-0.5">
              <span>
                <span className="text-gray-500 dark:text-gray-400">Miktar: </span>
                {fmtNum(header?.requestedQuantity ?? request.requestedQuantity)}
              </span>
              <span>
                <span className="text-gray-500 dark:text-gray-400">İstenen Teslim: </span>
                {(header?.requestedDeliveryDate ?? request.requestedDeliveryDate)
                  ? fmtDate(header?.requestedDeliveryDate ?? request.requestedDeliveryDate, false)
                  : '—'}
              </span>
            </div>
            <ManualRateNote info={detail?.exchangeRates} className="mt-1 inline-block" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative" ref={chooserRef}>
              <button
                type="button"
                onClick={() => setChooserOpen(o => !o)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                aria-label="Kolonlar"
              >
                <i className="fas fa-columns text-[10px]" />
                Kolonlar
              </button>
              {chooserOpen && (
                <div className="absolute right-0 mt-1 z-30 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg py-1 text-xs max-h-72 overflow-auto">
                  {ALL_COLUMNS.map(c => (
                    <label
                      key={c.colId}
                      className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={!hiddenColumns.has(c.colId)}
                        onChange={() => toggleColumn(c.colId)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-sm pt-0.5 pl-1"
              aria-label="Kapat"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tedarikçi listesi */}
        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center w-full h-full text-sm text-gray-500 dark:text-gray-400">
              Yükleniyor…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center w-full h-full text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : (
            <SupplierList
              quotes={quotes}
              hiddenColumns={hiddenColumns}
              gridStateKey="request-info-detail-rounds"
            />
          )}
        </div>
      </div>
      {ratePromptInfo && (
        <ExchangeRatePrompt
          info={ratePromptInfo}
          onSubmit={rates => {
            setRatePromptInfo(null);
            setManualRates(rates);
          }}
          onSkip={() => setRatePromptInfo(null)}
        />
      )}
    </div>
  );
};

// ─── Portal wrapper ──────────────────────────────────────────────────────────

const RequestInfoDetailModal: React.FC<{ request: ModalRequest; onClose: () => void }> = props =>
  ReactDOM.createPortal(<ModalInner {...props} />, document.body);

export default RequestInfoDetailModal;
